'use strict'

Vue.component('awe-map', {
  props: {
    locale: String,
    userLocation: {type: Object, default: () => {}},
    stores: Array,
    countries: Array,
    userMarkerColor: {type: String, default: '#f39027'},
    routeColor: {type: String, default: '#2d334e'},
    startVisible: {type: Boolean, default: false}, // if true, the map is visible on widget load
    noPopup: {type: Boolean, default: false}, // true to not show popup on store marker click
    fitToStores: {type: Number, default: 0}, // if set, number of stores (sorted by ascending distance) we'll use to fit the map
    fitToArea: {type: Number, default: 0}, // if set, fit map to area bounding box if at least N stores are within it.
    styleUri: {type: String, default: 'mapbox://styles/mapbox/streets-v10'},
    clusters: {type: Boolean, default: false}, // whether the map uses clusters
    clusterColor: {type: String, default: '#51bbd6'},
    clusterRadius: {type: Number, default: 20},
    clusterTextColor: {type: String, default: '#000000'},
    clusterBorderColor: {type: String, default: ''},
    clusterExclusionTags: {type: Array, default: () => []}, // stores with these tags will not be clustered
    allowFullSize: {type: Boolean, default: false}, // true to allow map to expand to widget's full size
    noClose: {type: Boolean, default: false}, // true to not render the close button
    reduceOnSelect: {type: Boolean, default: false}, // true to reduce fullscreen map when a store is selected
    error: {type: String, default: ''},
    showExpanded: {type: Boolean, default: null}, // if set, overrides the default value for opening expanded map option (API-3054)
    search: {type: String, default: 'on', validator: (val) => ['on', 'off', 'expanded'].includes(val)},
    defaultViewSelectedOnly: {type: Boolean, default: false}, // whether to show only selected store in default (non-expanded view)
    expandedViewSelectedOnly: {type: Boolean, default: false}, // whether to show only selected store in expanded view
    selectedRetailerOnly: {type: Boolean, default: false}, // whether to show only stores from the same retailer as the selected store
    allowOos: {type: Number, default: 0},
    routeMode: {type: String, default:'driving', validator: (val) => ['driving', 'walking', 'cycling'].includes(val)}
  },
  data: function(){
    return {
      id: null, // component id
      _map: null,
      _userMarker: null,
      storeMarkers: {},
      _popup: null,
      _popupContentNode: null,

      selected: null, // currently selected store
      i18n: this.$root.i18n,
      mapMoved: false, // whether the map was manually moved (dragged)
      expanded: false, // whether the map is expanded (takes all widget space)
      unclusteredStoreIds: [], // store ids that are excluded from clustering

      _storeUpdateTimeout: -1,
      _missedStoresUpdate: false,
      _mapboxLoaded: false, // whether mapbox scripts have loaded
      _mapboxPromise: null, // resolves when mapbox is loaded
      _mapCreated: false, // whether the map has been instanciated
      _mapCreationPromise: null, // resolves when map is instanciated
      _singleStoresDebouncer: -1, // timeout id for debouncing unclustered stores updates

      mapPadding: null, // padding when fitting map to markers

      // placeholder store
      defaultStore: {
        isDefault: true,
        product: {},
        retailer: {},
        store: { location: {}},
        price: {}
      },

      internalError: '',

      MAPBOX_SCRIPT: 'https://api.tiles.mapbox.com/mapbox-gl-js/v1.12.0/mapbox-gl.js',
      MAPBOX_STYLE: 'https://api.tiles.mapbox.com/mapbox-gl-js/v1.12.0/mapbox-gl.css'

    }
  },
  computed: {
    // get lang part from a locale (e.g. en-US => en)
    lang: function(){
      return this.locale.substring(0, 2)
    },
    userPos: function(){
      return [this.userLocation.longitude, this.userLocation.latitude]
    },
    // whether the map must be shown in expanded (full viewport) mode when opening.
    // true by default for media widgets, false otherwise. Can be overriden by setting showExpanded property.
    expandMap: function(){
      return app.isMedia && this.showExpanded != false || this.showExpanded
    },

    // whether to show the "search in this location" button
    showSearch: function(){
      if (!this.mapMoved || this.search === 'off')
        return false

      let val
      switch(this.search){
        case 'on': val = true; break
        case 'expanded': val = this.expanded; break
      }
      return val
    },
    containerStatusClasses: function(){
      return {
        expanded: this.expanded,
        'default-view-selected-only' : this.defaultViewSelectedOnly,
        'expanded-view-selected-only': this.expandedViewSelectedOnly,
        'selected-retailer-only': this.selectedRetailerOnly
      }
    }
  },
  watch: {
    // when component's parent is updated, all properties are refreshed with their latest value
    // (https://vuejs.org/v2/guide/components-props.html#One-Way-Data-Flow).
    // That means we sometimes get a userPos update with the same values. No need for update in that case.
    userLocation: function(val, old){
      if (val.longitude !== old.longitude || val.latitude !== old.latitude){
        if (this._userMarker)
          this._userMarker.setLngLat([val.longitude, val.latitude]) // can't use userPos, the computed function is not updated yet when the location watcher is triggered
        this.mapMoved = false
      }
      this.internalError = ''
    },

    // stores update are debounced. If not and 2 updates are too close (e.g. at first set), we may see collisions with multiple
    // nodes having same id/index which messes up, binding infowindow with the wrong marker
    stores: function(val, old){
      if (this._storeUpdateTimeout != -1)
        clearTimeout(this._storeUpdateTimeout)

      this._storeUpdateTimeout = setTimeout(() => {
        this._storeUpdateTimeout = -1

        if (this.clusters)
          this.setClusters()
        else
          this.setStoreMarkers()

        this.clearRoute()

      }, 300)
    },

    // when selected store changes:
    // - if selected-retailer-only option is enabled, inject style to hide stores from other retailers
    // - if clusters are enabled, reset clusters dataset to exclude the selected store
    selected: function(val, old){

      // change injected style only if selected-retailer-only option is enabled and selected store has a different retailer than previous selection
      if (this.selectedRetailerOnly && val.retailer.id && val.retailer.id !== old.retailer.id){
        const id = `map-${this.id}`,
              oldStyle = window[id],
              style = document.createElement('style')

        style.id = id
        style.innerText = `
          .selected-retailer-only .store-marker:not(.r-${val.retailer.id}){
            display: none !important;
          }
        `

        if (this.$el){
          if (oldStyle)
            this.$el.replaceChild(style, oldStyle)
          else
            this.$el.appendChild(style)
        }
      }

      // when selected store changes with clusters enabled, refresh them so that:
      // - other retailers are correctly discarded from clustering.
      // - selected store is excluded
      if (this.clusters)
        this.setClusters(false) // do not fit the map to content, so as to not change the zoom level.
    }
  },
  created: function(){
    this.selected = this.defaultStore
    this.id = '_' + Math.random().toString(36).substring(2)
  },
  mounted: function(){
    // HTML for popup is removed from the DOM and kept in memory. That way it's not displayed but still evaluated for reactive properties.
    this._popupContentNode = this.$el.removeChild(this.$el.querySelector('.map-popup-slot'))
  },

  methods: {
    // loads mapbox resources
    __load: function(){
      if (this.$data._mapboxPromise == null) {

        this.$data._mapboxPromise = new Promise((resolve, reject) => {
          let nd = document.createElement('script')
          nd.src = this.MAPBOX_SCRIPT
          nd.onload = () => {
            performance.measure('mapbox-load-time', 'mapbox-load-start')
            this.$data._mapboxLoaded = true
            console.debug(`Mapbox loaded in ${Utils.getPerf('mapbox-load-time')}ms`)
            return resolve()
          }
          nd.onerror = reject
          performance.mark('mapbox-load-start')
          document.head.appendChild(nd)

          nd = document.createElement('link')
          nd.href = this.MAPBOX_STYLE
          nd.setAttribute('rel', 'stylesheet')
          document.head.appendChild(nd)
        })
      }
      return this.$data._mapboxPromise
    },

    // show popup for the given availability
    show: async function(avail, showRoute = false, auto = false){
      // loads mapbox script once
      if (!this.$data._mapboxLoaded){
        try{
          await this.__load()
        }
        catch(err){
          console.error(err)
          throw new Error('Cannot load Mapbox')
        }
      }

      if (!this._mapCreated){
        try{
          await this.__init()
        }
        catch(err){
          throw new Error('Error instanciating map object')
        }
      }
      else if (!this.expandMap){
        // API-2964: changing geoloc resizes (hidden) map to default dimensions.
        // Force resize after map is made visible restores the actual dimensions.
        this._map.resize()
      }

      if (this.expandMap)
        this.toggleExpand()

      if (avail){
        // if availability has a nearestStore value, replace store object with nearestStore, since it's those values we want to see on a map.
        if (avail.nearestStore){
          avail.store = avail.nearestStore
          avail.nearestStore = null

          // if nearest store is closed for lockdown, the infowindow should show it.
          // Since lockdown prop is on the availability and not on the store prop, we need to find the availability that gave its store and use its lockdown value
          const nearest = this.stores.find(x => x.store.id === avail.store.id)
          if (nearest)
            avail.lockdown = nearest.lockdown
        }

        const newSelection = this.selected.retailer.id !== avail.retailer.id || this.selected.store.id !== avail.store.id
        this.selected = avail

        if (showRoute)
          this.getDirections(null, !!auto)
        else{
          // clear route only if selected store is different from the previous selection
          if (newSelection)
            this.clearRoute()

          // center map on store
          if (avail.store && avail.store.location){
            this._map.flyTo({
              center: [avail.store.location.longitude, avail.store.location.latitude]
            })
          }

          if (!this.noPopup)
            this._showPopup()
        }
      }

      // stores markers are set by watch on store list.
      // Set them manually only if we missed an update because the map wasn't ready
      if (this._missedStoresUpdate){
        if (this.clusters)
          this.setClusters()
        else
          this.setStoreMarkers()
      }
      // if an availability is selected with clusters enabled and selected store only option (default or expanded) is enabled,
      // refresh cluster source. That's because with these options, cluster data source consists only of selected store.
      else if (this.selected && this.clusters && this.defaultViewSelectedOnly && !this.expanded || this.expandedViewSelectedOnly && this.expanded)
        this.setClusters()

      // delay execution as markers set in setStoreMarkers are not available immediately in DOM
      setTimeout(this._styleSelectedMarker, 50)
    },

    // apply specific style to the selected marker
    _styleSelectedMarker: function(elem){
      if (!this.selected) return

      if (!elem){
        elem = this.$el.querySelector(`.store-marker[data-sid='${this.selected.store.id}']:not(.selected)`)
        if (!elem) return
      }

      const prevSelection = this.$el.querySelector('.store-marker.selected')
      if (prevSelection) // deselect previous selection
        prevSelection.classList.remove('selected')

      if (elem.dataset.alternateImg){
        // revert to normal image
        if (prevSelection){
          prevSelection.style.background = prevSelection.dataset.background
          delete prevSelection.dataset.background
        }

        elem.dataset.background = elem.style.background // save background property for de-selection
        elem.style.background = `url(${elem.dataset.alternateImg})`
      }

      elem.classList.add('selected')
    },

    // find the slotted element for the store marker, and clones it.
    // When the marker is removed from the map, the cloned node is removed, not the slotted one.
    // That way the slotted content is still there for rendering when stores change
    _getMarkerNode: function(storeId){
      var selector = `.store-markers .map-store-marker-content[data-sid='${storeId}']`
      var elem = this.$el.querySelector(selector)

      if (elem)
        return Promise.resolve(elem.cloneNode(true))

      return new Promise(resolve => {
        var i = 0
        var itv = setInterval(() => {
          elem = this.$el.querySelector(selector)
          if (elem){
            clearInterval(itv)
            return resolve(elem.cloneNode(true))
          }
          // timeout if not found after some time
          if (++i === 10){
            console.log(`Timeout: node ${storeId} not found`)
            clearInterval(itv)
            return resolve(null)
          }
        }, 100)
      })
    },

    // create a marker for the given store
    createMarker: async function(store){
      var markerElem = await this._getMarkerNode(store.store.id)
      if (!markerElem) return

      markerElem.setAttribute('role', 'img')

      // IE11 does not support multiple arguments for classList.add
      markerElem.classList.add('marker')
      markerElem.classList.add('store-marker')
      markerElem.dataset.sid = store.store.id // keep store id to allow retrieving the related store in list
      if (this.selectedRetailerOnly)
        markerElem.classList.add('r-' + store.retailer.id)
      markerElem.addEventListener('click', this.markerClickHandler)
      markerElem.addEventListener('keypress', this.markerClickHandler)

      var marker = new mapboxgl.Marker({
        element: markerElem,
        anchor: markerElem.getAttribute('anchor') || 'bottom'
      })

      marker.setLngLat([store.store.location.longitude, store.store.location.latitude])
      marker.addTo(this._map)
      this.storeMarkers[store.store.id] = marker
    },

    // event handler for click on marker
    markerClickHandler: function(e){
      e.stopPropagation()

      if (e.type === 'keypress' & e.key !== 'Enter')
        return

      e.currentTarget.focus()

      const sid = e.currentTarget.dataset.sid,
            store = this.stores.find(x => x.store.id === sid)

      if (!sid || !store)
        return console.debug(`Store ${sid} could not be found.`)

      this.selected = store
      this._styleSelectedMarker(e.currentTarget)

      if (!this.noPopup)
        this._showPopup()

      if (this.reduceOnSelect && this.expanded)
        this.toggleExpand()

      this.$emit('availability-select', {avail: this.selected})
      Tracker.dropTag('clkloc', {avail: this.selected, area: Area.MAP})
      this.$root.trigger('availability-select', {avail: this.selected, area: Area.MAP})
    },

    // shows markers for stores.
    // storeIds: list of store ids to display. If not provided, show all stores
    // fit: whether to adjust map viewport to show all markers
    setStoreMarkers: function(storeIds, fit = true){
      if (!this.stores) return

      if (!this._mapCreated){
        this._missedStoresUpdate = true
        return
      }
      this._missedStoresUpdate = false

      let stores
      if (storeIds)
        stores = this.stores.filter(x => storeIds.indexOf(x.store.id) > -1)
      else
        stores = this.stores

      // find store ids no longer in store list & remove associated marker
      const removedIds = Object.keys(this.storeMarkers).filter(sid => stores.find(x => x.store.id === sid) == null)
      removedIds.forEach(k => {
        this.storeMarkers[k].remove()
        delete this.storeMarkers[k]
      })

      stores.forEach(s => {
        // creates a marker if none exists for the store
        if (!this.storeMarkers[s.store.id])
          this.createMarker(s)
      })

      if (fit)
        this._fitMap()
    },

    // adjust map viewport to content: markers (all or subset) or area (e.g.country)
    // @fitCount (integer): number of stores to take for bounding box. Similar to fitToStores option
    _fitMap: function(fitCount = 0){
      // trying to fit with no stores fits only the user location, i.e. just a max zoom on the position.
      // Instead, we center the map on user position with a bounding box if provided, or a predefined zoom.
      if (this.stores.length === 0){
        if (this.userLocation.boundingBox){
          this._map.fitBounds(this.userLocation.boundingBox)
        }
        else{
          this._map.flyTo({
            center: this.userPos,
            zoom: 9
          })
        }
        return
      }

      // force zoom to location boundingbox if enough stores in it or if it's a country (& of course option is enabled and boundingbox exists)
      if (this.fitToArea && this.userLocation.boundingBox &&
          (Utils.countStoresInArea(this.userLocation.boundingBox, this.stores) >= this.fitToArea || this.userLocation.type === 'country')){
        // fit to provided box
        this._map.fitBounds(this.userLocation.boundingBox, {padding: 10})
      }
      else{
        // fit to stores (all or subset)
        let stores

        // fit to stores: fit only the N closest stores
        if (this.fitToStores || fitCount){
          const count = fitCount || this.fitToStores
          stores = this.stores.map(x => x ) // create a new array, so the sort does not modify the source property
                    .sort((a, b) => a.store.distance - b.store.distance) // sort by distance
                    .slice(0, count) // take only the requested number

          if (!this.selected.isDefault) // if a store is selected, include it in bounding box
            stores.push(this.selected)
        }
        else
          stores = this.stores

        // boundaries object is extended by adding positions that must be within the boundaries
        const bounds = new mapboxgl.LngLatBounds()

        for (let i = 0; i < stores.length; i++){
          let loc = stores[i].store.location
          // exclude stores without geoloc from boundaries
          if (loc.latitude != 0 || loc.longitude != 0)
            bounds.extend([loc.longitude, loc.latitude])
        }

        if (this.userPos)
          bounds.extend(this.userPos)

        if (this.mapPadding == null)
          this._computePadding()

        this._map.fitBounds(bounds, {padding: this.mapPadding})
      }
    },

    // Show popup for currently selected store
    _showPopup: function(){
      let newPopup = false

      // popup object is created once and moved/edited when displayed
      if (!this._popup){
        this._popup = new mapboxgl.Popup({
          closeOnClick: false,
          maxWidth: 'none',
          offset: {
            bottom: [0, -35],
            right: [-10, 0],
            left: [10, 0],
            'bottom-right': [0, -35],
            'bottom-left': [0, -35]
            // omitted positions do not need any offset
          }
        })
        newPopup = true
      }
      else{
        this._popup.remove()
      }

      // edit and position the popup
      this._popup.setDOMContent(this._popupContentNode)
        .setLngLat([this.selected.store.location.longitude, this.selected.store.location.latitude])
        .addTo(this._map)

      // listen to any click on popup and cancel propagation to avoid background click on media
      const popupNd = this.$el.getElementsByClassName('mapboxgl-popup-content')[0]
      if (popupNd)
        popupNd.addEventListener('click', e => { e.stopPropagation() })

      // trigger popup-init event after popup is added to the map. Otherwise it's not part of the DOM yet.
      if (newPopup)
        this.$root.trigger('popup-init')
      this.$root.trigger('popup-show')
    },

    // instanciates map object
    __init: function(){
      if (this.$data._mapCreationPromise == null){

        this.$data._mapCreationPromise = new Promise((resolve, reject) => {
          mapboxgl.accessToken = 'pk.eyJ1Ijoic3dhdmVuIiwiYSI6ImNqbTd1dXBlcjFhejMzcHBvajVlYjFjMXcifQ.ljVSrON2CjvnSj38Nxa9RA'
          this._map = new mapboxgl.Map({
            // don't use id, there may be multiple map instances in template.
            container: this.$el.getElementsByClassName('_map-ctnr')[0],
            style: this.styleUri,
            center: this.userPos,
            zoom: 10
          })

          this._map.on('load', () => {
            this._mapLoadCallback()
            resolve()
          })

        })
      }

      return this.$data._mapCreationPromise
    },

    _mapLoadCallback: function(){
      const mapboxStyle = this._map.getStyle()

      // We have to change manually the language for all layers.
      // Only change text fields and avoid changing road_label (ex: A1, D15, N25) because it's messing and useless
      mapboxStyle.layers.forEach(layer => {
        if (layer.layout?.['text-field'] && layer['source-layer'] != 'road_label'){
          this._map.setLayoutProperty(layer.id, 'text-field', ['get', 'name_' + this.lang])
        }
      })

      this._map.addControl(new mapboxgl.NavigationControl({showCompass: false}), 'bottom-right')

      if (this.clusters){
        this._initClusters()
      }

      this._mapCreated = true

      this.$root.trigger('map-init')
      this._createUserMarker()

      this._map.on('dragend', () => {
        const center = this._map.getCenter()
        if (Utils.getHaversineDistance(this.userPos, center) > MIN_DISTANCE )
          this.mapMoved = true
      })
    },

    // Configure, create & place user position marker
    _createUserMarker(){
      // this id is added to the node with attribute slot=user-marker by preprocessor
      var userMarkerContent = document.getElementById('user-marker'),
          markerOptions = {}

      if (userMarkerContent){
        userMarkerContent.setAttribute('role', 'img')
        markerOptions.element = userMarkerContent
        let anchor = userMarkerContent.getAttribute('anchor')
        if (anchor)
          markerOptions.anchor = anchor

        let display = userMarkerContent.getAttribute('display');
        
        // the manually created user marker is hidden by default so that it doesn't render before the map is ready
        // update the styling here to display it on the  map
        if (display) {
          userMarkerContent.setAttribute('display', 'block')
        }
      }
      else
        // default marker, custom color
        markerOptions.color = this.userMarkerColor

      // create, place and add marker to map
      this._userMarker = new mapboxgl.Marker(markerOptions)
      this._userMarker.setLngLat(this.userPos)
      this._userMarker.addTo(this._map)
    },

    clearRoute: function(){
      var routeLayer = this._map && this._map.getLayer('route')
      if (routeLayer){
        this._map.removeLayer('route') // Remove route drawn
        this._map.removeSource('route') // Remove source related to this layer
      }
    },

    getDirections: async function(e, auto = false){
      if (e && e.stopPropagation)
        e.stopPropagation()

      try{
        let route
        const straightline = Utils.getHaversineDistance(this.userPos, this.selected.store.location)
        let  mode = this.routeMode
        let retry = false

        do {
          const res = await fetch(`https://api.mapbox.com/directions/v5/mapbox/${mode}/${this.userPos[0]},${this.userPos[1]};${this.selected.store.location.longitude},${this.selected.store.location.latitude}?geometries=geojson&access_token=pk.eyJ1Ijoic3dhdmVuIiwiYSI6ImNqbTd1dXBlcjFhejMzcHBvajVlYjFjMXcifQ.ljVSrON2CjvnSj38Nxa9RA`),
                json = await res.json()
          route = json.routes[0]

          if (mode = 'driving' && route.distance >  10 * straightline) {
            console.debug('route too long, try walking.')
            mode = 'walking'
            retry = true
          }
          else {
            retry = false
          }
        }
        while (retry)

        const geometry = route.geometry

        this.clearRoute()

        this._map.addLayer({
          id: 'route',
          type: 'line',
          source: {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: geometry
            }
          },
          paint: {
            'line-width': 3,
            'line-color': this.routeColor
          }
        })

        // taken straight from https://www.mapbox.com/mapbox-gl-js/example/zoomto-linestring/
        let bounds = geometry.coordinates.reduce((bounds, coord) => {
          return bounds.extend(coord)
        }, new mapboxgl.LngLatBounds(geometry.coordinates[0], geometry.coordinates[0]))

        this._map.fitBounds(bounds, {padding: this.mapPadding})

        if (this._popup)
          this._popup.remove()
      }
      catch(err){
        console.error(err)
      }
      Tracker.dropTag('clkRoute', {avail: this.selected, area: Area.MAP, auto: !!auto})
      this.$root.trigger('route-show', {avail: this.selected})
    },

    close: function(e){
      e.stopPropagation()
      this.$emit('map-close')

      if (this.expanded) // reduce map (i.e. reduce iframe & widget) if currently fullscreen
        this.toggleExpand()
    },

    // set up clusters configuration: datasource, layer, event handling
    _initClusters: function(){
      // create empty datasource for store locations
      this._map.addSource('stores', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        },
        cluster: true,
        clusterMaxZoom: 13,
        clusterRadius: 50
      })

      const clusterPaint = {
        'circle-color': this.clusterColor,
        'circle-radius': this.clusterRadius,
      }
      if (this.clusterBorderColor){
        clusterPaint['circle-stroke-color'] = this.clusterBorderColor
        clusterPaint['circle-stroke-width'] = 1
      }

      // cluster layer
      this._map.addLayer({
        id: 'clusters',
        source: 'stores',
        type: 'circle',
        filter: ['has', 'point_count'],
        paint: clusterPaint,
      })

      // cluster count layer
      this._map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'stores',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': this.clusterTextColor

        }
      })

      // inspect a cluster on click
      this._map.on('click', 'clusters', e => {
        var features = this._map.queryRenderedFeatures(e.point, { layers: ['clusters'] })
        var clusterId = features[0].properties.cluster_id
        this._map.getSource('stores').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err)
            return

          this._map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom
          })
        })
      })

      // attach handler to all events that change clusters or make an unclustered store show/hide
      this._map.on('zoomend', () => { this.showUnclustered() })
      this._map.on('dragend', () => { this.showUnclustered() })
      this._map.on('sourcedata', e => {
        if (e.sourceId === 'stores')
          this.showUnclustered()
      })
    },

    // show markers for unclustered stores.
    // debounce to avoid doing the work too often.
    // Results are deduplicated since features returned by querySourceFeatures can contain duplicates.
    showUnclustered: function(){
      if (this._singleStoresDebouncer !== -1)
        clearTimeout(this._singleStoresDebouncer)

      this._singleStoresDebouncer = setTimeout(() => {
        this._singleStoresDebouncer = -1
        const singleStores = this._map.querySourceFeatures('stores', {filter: ['!has', 'point_count']})
        .map(x => x.properties.sid)
        .concat(this.unclusteredStoreIds) // add cluster-excluded stores to the list
        .reduce((dedups, feat) => {
          if (dedups.indexOf(feat) === -1)
            dedups.push(feat)
          return dedups
        }, [])

        this.setStoreMarkers(singleStores, false)
        setTimeout(this._styleSelectedMarker, 50)
      }, 100)
    },

    // Sort stores between the ones to cluster and the ones to exclude.
    // Returns list of stores to cluster. Unclustered store ids are in unclusteredStoreIds property. Stores to hide completely are discarded.
    _sortStoresForClustering: function(){
      const clusteredStores = []

      this.stores.forEach(s => {

        // if option selected-retailer-only is enabled, stores from retailers other than current selection's retailer are discarded
        if (this.selectedRetailerOnly && this.selected.retailer.id && this.selected.retailer.id !== s.retailer.id)
        return

        // stores with no tags are included
        if (!s.store.tags || s.store.tags.length === 0){
          clusteredStores.push(s)
          return
        }

        // find if store's tag array contains an exclusion tag
        let hasExclTag = false, i = 0
        while(!hasExclTag && i < s.store.tags.length){
          if (this.clusterExclusionTags.indexOf(s.store.tags[i++]) > -1)
            hasExclTag = true
        }

        if (hasExclTag)
          this.unclusteredStoreIds.push(s.store.id)
        else
          clusteredStores.push(s)
      })

      return clusteredStores
    },

    // sets the data source used to show store clusters
    setClusters: function(fit = true){
      if (!this._mapCreated){
        this._missedStoresUpdate = true
        return
      }
      this._missedStoresUpdate = false

      const src = this._map.getSource('stores')

      if (!this._mapCreated || !src){
        this._missedStoresUpdate = true
        return
      }
      this._missedStoresUpdate = false

      let clusteredStores // list of stores to add to cluster datasource
      this.unclusteredStoreIds = []

      if (this.clusterExclusionTags.length > 0 || this.selectedRetailerOnly){
        // get list of stores to cluster. Excluded stores are in unclusteredStoreIds property
        clusteredStores = this._sortStoresForClustering()
      }
      // take only selected if option is enabled
      else if (this.defaultViewSelectedOnly && !this.expanded || this.expandedViewSelectedOnly && this.expanded)
        clusteredStores = [this.selected]
      else
        // no exclusion tag defined, add all stores
        clusteredStores = this.stores

      // remove selected store from cluster dataset, and add it to unclustered stores
      if ( !this.selected.isDefault){
        clusteredStores = clusteredStores.filter(s => s.store.id !== this.selected.store.id)
        this.unclusteredStoreIds.push(this.selected.store.id)
      }

      // set stores source data
      src.setData({
        type: 'FeatureCollection',
        features: clusteredStores.map(x => {
          return {
            type: 'Feature',
            properties: {
              name: x.store.name,
              rid: x.retailer.id,
              lat: x.store.location.latitude,
              lng: x.store.location.longitude,
              sid: x.store.id,
            },
            geometry: {
              type: 'Point',
              coordinates: [x.store.location.longitude, x.store.location.latitude]
            }
          }
        })
      })

      if (fit)
        this._fitMap()
    },

    // sets new location from map center
    updateLocation: async function(evt){
      evt.stopPropagation()
      this.mapMoved = false // reset even if the location is ignored by app.

      const center = this._map.getCenter(),
            // don't filter results on enabled countries, we want to get location wherever it is
            loc = await Utils.getReverseGeocoding(center.lng, center.lat, [], this.lang, 'manual', false)

      if (!loc){
        console.error('Could not get address from ' + JSON.stringify(center))
        return
      }

      if (this.countries.includes(loc.country) || (this.allowOos & OosLevels.ALLOW) === OosLevels.ALLOW){
        this.$emit('location-update', loc)
        this.internalError = ''
      }
      else
        this.internalError = ErrorCodes.UNAVAILABLE_COUNTRY_ERROR
    },

    // toggle between full expanded map and normal size.
    // use inline style instead of a class because some templates might use higher-priority querySelector
    // that will override the expanded style. We could use a class with !important but that would prevent
    // implementations to customize the expanded style.
    toggleExpand: function(){
      this.expanded = !this.expanded

      // loader toggles iframe in full viewport size
      // hideHostScroll: tell loader to not restore scroll if the option is enabled
      hostCom.send('awe:toggleFullScreen', {hideHostScroll: this.$root.hideHostScroll && this.$root.isPopin}, () => {
        if (this.expanded){
          // app container may have inline dimensions (e.g. using fixed-size option),
          // change that to full viewport size
          let val = this.$root.$el.style.height
          this.$root.$el.style.height = '100vh'
          if (val)
            this.$root.$el.dataset.height = val

          val = this.$root.$el.style.width
          this.$root.$el.style.width = '100vw'
          if (val)
            this.$root.$el.dataset.width = val

          this.$el.style.position = 'fixed'
          this.$el.style.top = 0
          this.$el.style.left = 0
          this.$el.style.width = '100%'
          this.$el.style.height = '100%'
          this.$el.style['z-index'] = 50

          // bind escape keyup to reduce map
          this.$el.addEventListener('keyup', (e) => {
            if (e.key === 'Escape'){
              e.stopPropagation()
              this.toggleExpand()
            }
          }, {passive: true, once: true})
        }
        else {
          // reset container height/width to previous values
          let val = this.$root.$el.dataset.height || ''
          this.$root.$el.style.height = val
          val = this.$root.$el.dataset.width || ''
          this.$root.$el.style.width = val

          this.$el.style.position = ''
          this.$el.style.top = ''
          this.$el.style.left = ''
          this.$el.style.width = ''
          this.$el.style.height = ''
          this.$el.style['z-index'] = ''
        }

        // if display must change when toggling, redraw markers or clusters.
        // not applicable if both options are set, as stores to display do not change in that case.
        if (this.defaultViewSelectedOnly !== this.expandedViewSelectedOnly){
          if (this.clusters)
            this.setClusters()
          else
            this.setStoreMarkers()
        }

        setTimeout(() => {
          this._map.resize()
          this._fitMap()
        }, 50)
      })
    },

    // resize the map
    resize: function(){
      if (this._mapCreated)
        this._map.resize()
    },

    // determines the appropriate padding to use when fitting map to markers.
    // looks for css rules with the marker class, and take the highest height/width values
    _computePadding: function(){
      let rules = []

      try{
        // find rules that applies to markers
        for (let i  = 0; i < document.styleSheets.length; i++){
          let sheet = document.styleSheets[i]

          // avoid security error in Firefox when trying to look rules from another origin
          if (sheet.href && !sheet.href.startsWith(window.origin))
            continue

          for (let j = 0; j < sheet.cssRules.length; j++){
            let rule = sheet.cssRules[j]
            if (rule.type === CSSRule.STYLE_RULE && rule.selectorText.indexOf('.map-store-marker-content') != -1)
              rules.push(rule)
          }
        }
      }
      catch(ex){
        Tracker.log('error', {msg: ex.message, stack: ex.stack})
        console.error(ex.message)
      }

      let width = 0, height = 0

      // look rules style and take highest height & width values
      rules.forEach(r => {
        let w = r.style.width,
            h = r.style.height

        if (w.endsWith('px'))
          width = Math.max(width, parseInt(w, 10))
          if (h.endsWith('px'))
            height = Math.max(height, parseInt(h, 10))
      })

      if (height && width){
        let horizontalPadding = Math.floor(width / 2) + 10
        this.mapPadding = {
          top: height + 10,
          right: horizontalPadding,
          bottom: 10,
          left: horizontalPadding
        }
      }
      else
        this.mapPadding = 50 // default value
    },

    // resets selected availability
    clearSelection: function(){
      this.selected = this.defaultStore
    },

    // just an alias to the app's buy method
    buy: app.buy
  },

  template: `
    <div style="overflow:hidden;" :class="containerStatusClasses">
      <div class="_map-ctnr"></div>

      <slot name="popup" v-bind:selected="selected">
        <div class='map-popup-slot' role="dialog">
          <div class="map-block-1" role="presentation">
            <img :src="selected.retailer.logo" :alt="selected.retailer.name">
            <span>{{ selected.store.name }}</span>
          </div>
          {{ selected.store.location.address }}
          <div class="map-btns" role="presentation">
            <a v-if="selected.clkUrl" v-on:click="buy(selected, $event)" :href="selected.clkUrl" target="_blank" class="map-btn">{{i18n.getString('BUY')}}</a>
            <span v-on:click="getDirections" v-on:keypress.enter="getDirections" class="map-btn" role="button">{{i18n.getString('SHOWROUTE')}}</span>
          </div>
        </div>
      </slot>

      <div class="store-markers">
        <slot name="store-marker" v-for="store in stores" v-bind:store="store">
          <div class="map-store-marker-content" :data-sid="store.store.id" :style="{background:'url(//content.static-swaven.com/img/picto_map/' + store.retailer.logoId + '.png)'}">
          </div>
        </slot>
      </div>

      <slot name="user-marker"></slot>

      <div class="map-controls">
        <div v-if="!noClose" class="control map-close icon-cancel-1" v-on:click="close" v-on:keypress.enter="close"
          role="button" :aria-label="i18n.getString('MAP_CLOSE_LBL')"></div>
        <div v-if="allowFullSize" class="control map-expand" :class="expanded ? 'icon-resize-small' : 'icon-resize-full'"
          v-on:click="toggleExpand" v-on:keypress.enter="toggleExpand"
          role="button" :aria-label="i18n.getString(expanded ? 'MAP_REDUCE_LBL' : 'MAP_EXPAND_LBL')"></div>
      </div>

      <transition name="fade">
        <div id="map-messages" v-show="mapMoved || error || internalError">
          <div v-if="error || internalError" class="map-error">
            {{i18n.getString(error || internalError)}}
          </div>
          <div v-show="showSearch" v-on:click="updateLocation" v-on:keypress.enter="updateLocation" class="map-moved-btn">{{i18n.getString('UPDATE_LOCATION')}}</div>
        </div>
      </transition>
    </div>
  `
})
